import {
    FC,
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useInitialize } from '@kameleoon/react-sdk';
import cookie from 'cookie';
import { cloneDeep, merge } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useLocation, useNavigate } from 'react-router-dom';
import { BaseSchema } from 'yup';

import { trackCometlyFactFindStarted } from '@App/cometly/cometly';
import { useSentryLogger } from '@App/hooks/log';
import { useIsNeedsAndPreferencesComplete } from '@App/hooks/useIsNeedsAndPreferencesComplete';
import { IntercomDataModel } from '@App/intercom/intercom-data.model';
import { ApplicationFinalStage } from '@App/interfaces/application-final-stage';
import { ApplicationStage } from '@App/interfaces/application-stage.enum';
import { Offer, RecommendedOffers } from '@App/interfaces/offer';
import {
    getApplicationById,
    getPublicApplicationData,
    getUserApplication,
} from '@App/requests/getApplicationData';
import { getDecision } from '@App/requests/getOfferData';
import { patchApplicationStage } from '@App/requests/patchApplicationStage';
import { patchStageToFactFind } from '@App/requests/patchStageToFactFind';
import { saveAnswers } from '@App/requests/questionnaires';
import { runCreditCommitmentsReport } from '@App/requests/runCreditCommitments';
import {
    saveApplicantsData,
    saveIncomingsData,
    saveLoanDetailsData,
    saveOutgoingsData,
    savePropertyDetailsData,
} from '@App/requests/saveApplicationData';
import {
    transformApplicantToRequest,
    transformApplicants,
    transformIncomingsToRequest,
    transformLoanDetailsToRequest,
    transformOutgoingsToRequest,
    transformPropertyDetailsToRequest,
} from '@App/requests/transformToRequest';
import { loanPreferencesFormItems } from '@FormModels/eligibility/loanPreferences';
import { getValidationSchema } from '@Shared/helpers/input';
import { formItems as applicantFormItems } from '@src/app/formModels/eligibility/applicantDetails';
import { formItems as incomingsFM } from '@src/app/formModels/eligibility/incomings';
import { formItems as loanFormItems } from '@src/app/formModels/eligibility/loanDetails';
import { formItems as outgoingsFormItems } from '@src/app/formModels/eligibility/outgoings';
import { formItems as propertyFormItems } from '@src/app/formModels/eligibility/yourProperty';
import { savePhoneNumber } from '@src/app/requests/saveApplicationData';
import { checkForDeclinedOffer } from '@src/shared/helpers/offer';
import { containsAnySubStr } from '@src/shared/helpers/string';
import { routesToSkip } from '@src/shared/lists/routes';
import { FormStorage } from '@src/shared/types';
import useStorage from '@src/ui/hooks/useStorage';

import { postStoredApplication } from '../requests/saveLSData';
import {
    transformFromRequest,
    transformIntercomDataFromRequest,
    transformIntercomName,
} from '../requests/transformFromRequest';
import {
    removeValidationProperty,
    updateState,
    validateIncomings,
} from '../utils/application';

import { AuthContext } from './Auth';

export const ApplicationContext = createContext<any>({ loading: true });
interface ApplicationContextProviderProps {
    children?: ReactNode;
    getAccessToken: Function;
    isToFetchApplication: boolean | null;
    getIdToken: Function;
}

const ApplicationContextProvider: FC<ApplicationContextProviderProps> = ({
    children,
    getAccessToken,
    isToFetchApplication,
    getIdToken,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { loggedIn } = useContext(AuthContext);

    const [loanDetails, setLoanDetails] = useState<any>({});
    const [applicants, setApplicants] = useState<any>([{}]);
    const [propertyDetails, setPropertyDetails] = useState<any>({});
    const [outgoings, setOutgoings] = useState<any>({});
    const [incomings, setIncomings] = useState<any>([]);
    const [underwriting, setUnderwriting] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [getAppData, setGetAppData] = useState(false);

    const [activeApplicant, setActiveApplicant] = useState<number | null>(null);
    const [applicationId, setApplicationId] = useState('');
    const [applicationEmail, setApplicationEmail] = useState('');
    const [offers, setOffers] = useState<Offer[]>([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [callDateTime, setCallDateTime] = useState<string | null>(null);
    const [applicationStage, setApplicationStage] = useState<
        ApplicationStage | ''
    >('');
    const [hasDashboardError, setHasDashboardError] = useState<boolean>(false);
    const [currentForm, setCurrentForm] = useState<any>(null);
    const [intercomData, setIntercomData] = useState<IntercomDataModel>({
        email: '',
        name: '',
        phone: '',
        applicationId: '',
    });

    const [isApplicationUpdated, setIsApplicationUpdated] =
        useState<boolean>(false);

    const [appLs, setAppLs] = useStorage<FormStorage>('local', 'form');
    const [externalApplicationId, setExternalApplicationId] = useState('');

    const { initialize } = useInitialize();

    const [debtConUser] = useState<boolean | null>(false);

    // Switched off debt con page due to deemed it to be non-compliant by compliance.
    // Will be enabled again when additional controls will be reviewed and added

    // useEffect(() => {
    //     setDebtConUser(
    //         loanDetails?.facilities
    //             ? loanDetails.facilities.some(
    //                   (f: any) => f?.allocationPurpose === 'Debt consolidation',
    //               )
    //             : null,
    //     );
    // }, [loanDetails?.facilities]);

    const [needsAndPreferencesCompleted, setNeedsAndPreferencesCompleted] =
        useState<boolean | null>(null);
    const [secondApplicantDeferred, setSecondApplicantDeferred] = useState<
        boolean | null
    >(null);

    const [preferableOffers, setPreferableOffers] = useState<any>(null);
    const [recommendedOffers, setRecommendedOffers] =
        useState<RecommendedOffers | null>(null);

    const [hasDeclinedOffer, setHasDeclinedOffer] = useState<boolean | null>(
        null,
    );

    var readyForQuote = false;

    const { sentryLog, handleError, flushLogs } = useSentryLogger(
        applicationId,
        'Application context',
    );

    // clears any a2_defer_variant/call_booking_variant left from a previous application
    useEffect(() => {
        mixpanel.unregister('a2_defer_variant');
        mixpanel.unregister('call_booking_variant');
    }, []);

    useIsNeedsAndPreferencesComplete({
        applicationEmail,
        applicationId,
        setNeedsAndPreferencesCompleted,
        setLoading,
        applicationStage,
        setRecommendedOffers,
        getAccessToken,
        getIdToken,
        hasDeclinedOffer,
    });

    const dashboardItemStatuses = useMemo(() => {
        const dataMap: any = [
            {
                name: 'loan-details',
                itemSpecs: loanFormItems,
                data: [loanDetails],
            },
            {
                name: 'applicants',
                itemSpecs: applicantFormItems,
                data: applicants,
            },
            {
                name: 'property-details',
                itemSpecs: propertyFormItems,
                data: [propertyDetails],
            },
            {
                name: 'income',
                itemSpecs: incomingsFM,
                data: incomings,
            },
            {
                name: 'outgoings',
                itemSpecs: outgoingsFormItems,
                data: [outgoings],
            },
            {
                name: 'debt-con',
            },
            {
                name: 'loan-preferences',
                itemSpecs: loanPreferencesFormItems,
            },
            {
                name: 'quote',
                data: { applicationStage, offers },
            },
        ];

        let prevIsDisabled = false;

        const isApplicationRejected = containsAnySubStr(
            applicationStage || '',
            [ApplicationStage.DECISIONING_DECLINE, ApplicationStage.CLOSED],
        );

        const isApplicationAccepted = containsAnySubStr(
            applicationStage || '',
            [ApplicationStage.DECISIONING_ACCEPT],
        );

        const hasOffer =
            (isApplicationAccepted || isApplicationRejected) &&
            offers &&
            isToFetchApplication;

        const canViewQuote = () => {
            if (isApplicationRejected) {
                return true;
            }

            return hasOffer && needsAndPreferencesCompleted;
        };

        return dataMap.map((dataItem: any) => {
            // disables steps before N&P
            if (
                dataItem.name !== 'loan-preferences' &&
                dataItem.name !== 'quote' &&
                hasOffer
            ) {
                return {
                    name: dataItem.name,
                    completed: true,
                    disabled: true,
                };
            }

            // disables steps before quote
            if (canViewQuote()) {
                if (dataItem.name === 'quote') {
                    return {
                        name: dataItem.name,
                        completed: true,
                        disabled: false,
                    };
                }

                return {
                    name: dataItem.name,
                    completed: true,
                    disabled: true,
                };
            }

            let status;

            try {
                const schema = getValidationSchema(dataItem.itemSpecs);

                const isApplicantsWithData =
                    dataItem.name === 'applicants' && dataItem.data;
                const isIncomeWithData =
                    dataItem.name === 'income' && dataItem.data;

                const differentItemCount =
                    dataItem.data.length === 1 && applicants.length === 2;

                if (isApplicantsWithData && differentItemCount) {
                    throw new Error('Income data is not valid');
                }
                if (isIncomeWithData && differentItemCount) {
                    throw new Error('Applicants data is not valid');
                }

                dataItem.data.forEach((data: BaseSchema) => {
                    // validateIncomings returns false if incomings is not valid as it's used other places
                    if (
                        dataItem.name === 'income' &&
                        !validateIncomings(data)
                    ) {
                        throw new Error('Incoming validation failed');
                    }

                    // Skip validation for property otherOwnershipSchemeType if it's undefined
                    if (
                        dataItem.data[0].ownershipSchemeType === 'Other' &&
                        dataItem.data[0].otherOwnershipSchemeType === undefined
                    ) {
                        removeValidationProperty(propertyFormItems);
                    }

                    // validateSync will throw error if not valid
                    return schema.validateSync(data);
                });
                status = {
                    name: dataItem.name,
                    completed: true,
                    disabled: prevIsDisabled,
                };
            } catch (e: any) {
                status = {
                    name: dataItem.name,
                    completed: false,
                    disabled: prevIsDisabled,
                };
                prevIsDisabled = true;
            }
            return status;
        });
    }, [
        loanDetails,
        applicants,
        propertyDetails,
        incomings,
        outgoings,
        applicationStage,
        offers,
        isToFetchApplication,
        needsAndPreferencesCompleted,
    ]);

    const loadLocalStorageData = (appId: string) => {
        appLs?.[appId]?.factFind.loanDetails &&
            setLoanDetails(appLs[appId].factFind.loanDetails);
        appLs?.[appId]?.factFind.applicants &&
            setApplicants(appLs[appId].factFind.applicants);
        appLs?.[appId]?.factFind.incomings &&
            setIncomings(appLs[appId].factFind.incomings);
        appLs?.[appId]?.factFind.propertyDetails &&
            setPropertyDetails(appLs[appId].factFind.propertyDetails);
        appLs?.[appId]?.factFind.outgoings &&
            setOutgoings(appLs[appId].factFind.outgoings);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                await initialize();
            } catch (error) {
                sentryLog('Kameleoom Initialization failed: ' + error);
            }

            sentryLog('isToFetchApplication ' + isToFetchApplication);

            if (isToFetchApplication === true) {
                setLoading(true);
                setHasDashboardError(false);

                const response = await fetchApplicationData();
                if (!response.success) {
                    console.error(
                        '[Dashboard] Error fetching the application: ',
                        response.error,
                    );
                    setHasDashboardError(true);
                }
            } else if (
                isToFetchApplication === false &&
                !localStorage.getItem('user.email')
            ) {
                const cookies = cookie.parse(document.cookie);

                if (!cookies.application) {
                    sentryLog('unable to retrive cookie data');
                    return;
                }
                const appInfo = JSON.parse(cookies.application);
                const appId = appInfo.applicationId;
                const email = appInfo.emailAddress;

                setApplicationId(appId);
                setApplicationEmail(email);
                trackCometlyFactFindStarted(appId, email);

                if (
                    location.pathname === '/' ||
                    [...routesToSkip].some((route) =>
                        location.pathname.includes(route),
                    )
                ) {
                    loadLocalStorageData(appId);
                    return;
                }

                await fetchPublicApplicationData(appId, email);

                loadLocalStorageData(appId);
            }
        };

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isToFetchApplication]);

    const fetchApplicationData = async () => {
        try {
            setIsApplicationUpdated(false);
            setGetAppData(true);
            setLoading(true);
            sentryLog('fetching application data');
            const response = await getUserApplication(
                await getAccessToken(),
                await getIdToken(),
            );

            if (response.success) {
                sentryLog('fetching application data successfull');

                const {
                    applicants,
                    propertyDetails,
                    loanDetails,
                    outgoings,
                    stage,
                    id,
                    emailAddress,
                    incomings,
                    offers,
                    externalApplicationId,
                    underwriting,
                    secondApplicantDeferred,
                } = transformFromRequest(response);

                // update cookie to avoid logging user out when there is a mismatch with prev application
                const updatedData = {
                    applicationId: id,
                    emailAddress,
                };

                document.cookie = cookie.serialize(
                    'application',
                    JSON.stringify(updatedData),
                    {
                        path: '/',
                        domain: window._env_.SELINA_DOMAIN,
                    },
                );

                mixpanel.identify(emailAddress);
                mixpanel.people.set({ $applicationId: id });

                sentryLog('stage ' + stage);
                sentryLog(
                    'outgoings complete ' + dashboardItemStatuses[4].completed,
                );
                sentryLog(
                    'quote complete ' + dashboardItemStatuses[5].completed,
                );

                if (stage === ApplicationStage.DECISIONING_ACCEPT) {
                    const hasDeclined = checkForDeclinedOffer(response.offers);

                    setHasDeclinedOffer(hasDeclined);
                }

                const handleApplicationState = () => {
                    if (
                        secondApplicantDeferred &&
                        applicants.length === 1 &&
                        applicants[0]?.addresses?.[0]
                    ) {
                        applicants.push({
                            addresses: [applicants[0].addresses[0]],
                        });
                    }

                    updateState(applicants, propertyDetails);

                    setApplicationId(id);
                    localStorage.setItem('user.applicationId', id);

                    setApplicationEmail(emailAddress);
                    setExternalApplicationId(externalApplicationId);
                    setApplicants(applicants);
                    setPropertyDetails(propertyDetails);
                    setLoanDetails(loanDetails);
                    setOutgoings(outgoings);
                    setApplicationStage(stage);
                    setIncomings(incomings);
                    setUnderwriting(underwriting);
                    setSecondApplicantDeferred(secondApplicantDeferred);
                    setOffers(offers);
                    setIntercomData(
                        transformIntercomDataFromRequest({
                            ...response.applicants[0],
                            applicationId,
                        }),
                    );
                    const callDateTime =
                        response?.adviceCall?.eventMemberships?.[0]
                            ?.bufferedStartTime;
                    setCallDateTime(callDateTime);
                    setSelectedProduct(response.selectedProduct);
                };

                if (!readyForQuote) {
                    const formSaveResult = await saveLSFormData(id);
                    if (!formSaveResult) {
                        handleApplicationState();
                    }
                } else {
                    handleApplicationState();
                }

                const isDeferredCoApplicantPending =
                    secondApplicantDeferred === true &&
                    !validateIncomings(incomings?.[1]);

                if (
                    dashboardItemStatuses[4].completed &&
                    !dashboardItemStatuses[5].completed &&
                    !debtConUser &&
                    !isDeferredCoApplicantPending
                ) {
                    sentryLog('navigate to wait');
                    navigate('/eligibility/wait');
                }
            }
            return response;
        } catch (e) {
            return handleError(e, 'fetch application data');
        } finally {
            setGetAppData(false);
            setLoading(false);
            setIsApplicationUpdated(true);
        }
    };

    const fetchPublicApplicationData = async (
        applicationId: string,
        emailAddress: string,
    ) => {
        try {
            setLoading(true);

            mixpanel.identify(emailAddress);
            mixpanel.people.set({ $applicationId: applicationId });

            sentryLog('fetching public application data');
            const response = await getPublicApplicationData(
                applicationId,
                emailAddress,
            );
            if (response.success) {
                sentryLog('fetching public application data successfull');
                const {
                    applicants,
                    propertyDetails,
                    loanDetails,
                    outgoings,
                    stage,
                    incomings,
                } = transformFromRequest(response);

                setApplicants(applicants);
                setPropertyDetails(propertyDetails);
                setLoanDetails(loanDetails);
                setOutgoings(outgoings);
                setApplicationStage(stage);
                setIncomings(incomings);
                setIntercomData(
                    transformIntercomDataFromRequest({
                        ...response.applicants[0],
                        applicationId,
                    }),
                );
            } else {
                sentryLog('fetching public application data not successfull');
            }
        } catch (e) {
            return handleError(e, 'fetch public application data');
        } finally {
            setLoading(false);
        }
    };

    const saveLoanDetails = async (data: any) => {
        const updateState = () => {
            const tmpLoanDetails = {
                ...data,
            };

            const tmpApplicants = [...applicants];
            const tmpIncomings = [...incomings];

            const numberOfApplicants = data.applicants;

            if (numberOfApplicants === 2 && tmpApplicants.length === 1) {
                tmpApplicants.push({
                    addresses: [tmpApplicants[0].addresses[0]],
                });
            }
            if (numberOfApplicants === 1 && tmpApplicants.length === 2) {
                tmpApplicants.pop();
                tmpIncomings.pop();
            }

            setLoanDetails(tmpLoanDetails);
            setApplicants(tmpApplicants);
            setIncomings(tmpIncomings);

            return {
                loanDetails: tmpLoanDetails,
                applicants: tmpApplicants,
                incomings: tmpIncomings,
            };
        };
        try {
            setLoading(true);

            if (loggedIn) {
                const response = await saveLoanDetailsData(
                    {
                        accessToken: await getAccessToken(),
                        idToken: await getIdToken(),
                    },
                    transformLoanDetailsToRequest(cloneDeep(data)),
                    applicationId,
                );
                updateState();
                return response;
            } else {
                const res = updateState();

                if (applicationId) {
                    setAppLs((prevData) => {
                        return {
                            ...prevData,
                            [applicationId]: {
                                ...(prevData?.[applicationId]
                                    ? prevData[applicationId]
                                    : {}),
                                factFind: {
                                    ...(prevData?.[applicationId]
                                        ? prevData[applicationId].factFind
                                        : {}),
                                    ...res,
                                },
                                factFindForm: {
                                    ...(prevData?.[applicationId]
                                        ? prevData[applicationId].factFindForm
                                        : {}),
                                    loanDetails: transformLoanDetailsToRequest(
                                        cloneDeep(data),
                                    ),
                                },
                            },
                        };
                    });
                }

                return {
                    success: true,
                };
            }
        } catch (e) {
            return handleError(e, 'save loan details');
        } finally {
            setLoading(false);
        }
    };

    const saveApplicants = async (
        data: any,
        isPrimaryApplicant: boolean,
        numberOfApplicants: number,
    ) => {
        const hasCoApplicant = numberOfApplicants === 2 && isPrimaryApplicant;

        const updateState = () => {
            const localApplicants: any[] = [data];

            if (
                numberOfApplicants === 2 &&
                isPrimaryApplicant &&
                !applicants[1]?.addresses
            ) {
                localApplicants.push({
                    addresses: [localApplicants[0].addresses[0]],
                });
            }

            if (
                numberOfApplicants === 2 &&
                isPrimaryApplicant &&
                applicants[1]
            ) {
                localApplicants.push(applicants[1]);
            }

            if (numberOfApplicants === 2 && !isPrimaryApplicant) {
                localApplicants.unshift(applicants[0]);
            }

            setApplicants(localApplicants);

            if (isPrimaryApplicant) {
                setIntercomData((previousData: IntercomDataModel) => ({
                    ...previousData,
                    name: transformIntercomName(localApplicants[0]),
                    applicationId: applicationId,
                }));
            }

            return { applicants: localApplicants };
        };
        try {
            setLoading(true);

            if (loggedIn) {
                const requestData = transformApplicants(
                    numberOfApplicants,
                    cloneDeep(applicants),
                    cloneDeep(data),
                    isPrimaryApplicant,
                );
                const response = await saveApplicantsData(
                    {
                        accessToken: await getAccessToken(),
                        idToken: await getIdToken(),
                    },
                    requestData,
                    applicationId,
                );

                updateState();

                // Set active applicant only if the request was executed with success
                if (response.success && hasCoApplicant) {
                    setActiveApplicant(1);
                }

                return { ...response, hasCoApplicant };
            } else {
                const res = updateState();

                setAppLs((prevData) => ({
                    ...prevData,
                    [applicationId]: {
                        ...(prevData[applicationId]
                            ? prevData[applicationId]
                            : {}),
                        factFind: {
                            ...prevData[applicationId].factFind,
                            ...res,
                        },
                        factFindForm: {
                            ...prevData[applicationId].factFindForm,
                            applicants: transformApplicants(
                                numberOfApplicants,
                                cloneDeep(applicants),
                                cloneDeep(data),
                                isPrimaryApplicant,
                            ),
                        },
                    },
                }));

                // Set active applicant only if the request was executed with success
                if (hasCoApplicant) {
                    setActiveApplicant(1);
                }

                return {
                    success: true,
                    hasCoApplicant,
                };
            }
        } catch (e) {
            return handleError(e, 'save applicants');
        } finally {
            setLoading(false);
        }
    };

    const saveIncomings = async (
        data: any,
        isPrimaryApplicant: boolean,
        noOfApplicants?: number,
    ) => {
        const numberOfApplicants = noOfApplicants || loanDetails.applicants;
        const hasCoApplicant = numberOfApplicants === 2 && isPrimaryApplicant;

        const updateState = () => {
            const localIncomings: any[] = [data];

            if (
                numberOfApplicants === 2 &&
                isPrimaryApplicant &&
                incomings[1]
            ) {
                localIncomings.push(incomings[1]);
            }

            if (numberOfApplicants === 2 && !isPrimaryApplicant) {
                localIncomings.unshift(incomings[0]);
            }

            setIncomings(localIncomings);

            if (hasCoApplicant) {
                setActiveApplicant(1);
            }

            return { incomings: localIncomings };
        };

        try {
            setLoading(true);

            if (loggedIn) {
                const requestData = transformIncomingsToRequest(
                    cloneDeep(data),
                );
                const response = await saveIncomingsData(
                    {
                        accessToken: await getAccessToken(),
                        idToken: await getIdToken(),
                    },
                    requestData,
                    applicationId,
                    isPrimaryApplicant ? 1 : 2,
                );

                updateState();

                return { ...response, hasCoApplicant };
            } else {
                const res = updateState();

                if (isPrimaryApplicant) {
                    setAppLs((prevData) => ({
                        ...prevData,
                        [applicationId]: {
                            ...(prevData[applicationId]
                                ? prevData[applicationId]
                                : {}),
                            factFind: {
                                ...prevData[applicationId].factFind,
                                ...res,
                            },
                            factFindForm: {
                                ...prevData[applicationId].factFindForm,
                                incomings: [
                                    transformIncomingsToRequest(
                                        cloneDeep(data),
                                    ),
                                    ...(prevData[applicationId].factFindForm
                                        .incomings &&
                                    prevData[applicationId].factFindForm
                                        .incomings.length > 1 &&
                                    Array.isArray(
                                        prevData[applicationId].factFindForm
                                            .incomings[1],
                                    )
                                        ? prevData[applicationId].factFindForm
                                              .incomings[1]
                                        : []),
                                ],
                            },
                        },
                    }));
                } else {
                    setAppLs((prevData) => ({
                        ...prevData,
                        [applicationId]: {
                            factFind: {
                                ...prevData[applicationId].factFind,
                                ...res,
                            },
                            factFindForm: {
                                ...prevData[applicationId].factFindForm,
                                incomings: [
                                    prevData[applicationId].factFindForm
                                        .incomings[0],
                                    transformIncomingsToRequest(
                                        cloneDeep(data),
                                    ),
                                ],
                            },
                        },
                    }));
                }

                return {
                    success: true,
                    hasCoApplicant,
                };
            }
        } catch (e) {
            return handleError(e, 'save incomings');
        } finally {
            setLoading(false);
        }
    };

    const savePropertyDetails = async (data: any) => {
        try {
            setLoading(true);

            if (loggedIn) {
                const response = await savePropertyDetailsData(
                    {
                        accessToken: await getAccessToken(),
                        idToken: await getIdToken(),
                    },
                    transformPropertyDetailsToRequest(data),
                    applicationId,
                );
                setPropertyDetails(data);
                return response;
            } else {
                setAppLs((prevData) => ({
                    ...prevData,
                    [applicationId]: {
                        ...(prevData[applicationId]
                            ? prevData[applicationId]
                            : {}),
                        factFind: {
                            ...prevData[applicationId].factFind,
                            propertyDetails: data,
                        },
                        factFindForm: {
                            ...prevData[applicationId].factFindForm,
                            propertyDetails:
                                transformPropertyDetailsToRequest(data),
                        },
                    },
                }));

                setPropertyDetails(data);

                return {
                    success: true,
                };
            }
        } catch (e) {
            return handleError(e, 'save property details');
        } finally {
            setLoading(false);
        }
    };

    const saveOutgoings = async (data: any) => {
        const transformedOutgoingsData = transformOutgoingsToRequest(data);
        const transformedApplicants = applicants.map(
            (applicant: any, index: number) =>
                transformApplicantToRequest(
                    applicant,
                    index === 0,
                    loanDetails,
                ),
        );
        transformedApplicants[0].numberOfAdultDependants =
            transformedOutgoingsData.numberOfAdultDependants;
        transformedApplicants[0].numberOfChildDependants =
            transformedOutgoingsData.numberOfChildDependants;

        const transformedData = {
            expenditure: transformedOutgoingsData.expenditure,
            applicants: transformedApplicants,
            ...transformedOutgoingsData.consent,
        };

        const updateState = () => {
            setOutgoings(data);

            const tmpApplicants = [...applicants];
            tmpApplicants[0].numberOfAdultDependants =
                transformedData.applicants[0].numberOfAdultDependants;
            tmpApplicants[0].numberOfChildDependants =
                transformedData.applicants[0].numberOfChildDependants;
            setApplicants(tmpApplicants);

            return { outgoings: data, applicants: tmpApplicants };
        };

        try {
            setLoading(true);

            if (loggedIn) {
                const response = await saveOutgoingsData(
                    {
                        accessToken: await getAccessToken(),
                        idToken: await getIdToken(),
                    },
                    transformedData,
                    applicationId,
                );

                updateState();

                return response;
            } else {
                const res = updateState();

                setAppLs((prevData) => ({
                    ...prevData,
                    [applicationId]: {
                        ...(prevData[applicationId]
                            ? prevData[applicationId]
                            : {}),
                        factFind: {
                            ...prevData[applicationId].factFind,
                            ...res,
                        },

                        factFindForm: {
                            ...prevData[applicationId].factFindForm,
                            outgoings: transformedData,
                        },
                    },
                }));

                return {
                    success: true,
                };
            }
        } catch (e) {
            return handleError(e, 'save outgoings');
        } finally {
            setLoading(false);
        }
    };

    const fetchApplication = async () => {
        try {
            return await getApplicationById(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
            );
        } catch (e) {
            return handleError(e, 'fetch application');
        }
    };

    const runDecisioning = async () => {
        try {
            return await getDecision(
                await getAccessToken(),
                await getIdToken(),
                applicationId,
            );
        } catch (e) {
            return handleError(e, 'run decisioning');
        }
    };

    const runCreditCommitments = async () => {
        try {
            setLoading(true);
            return await runCreditCommitmentsReport(
                await getAccessToken(),
                await getIdToken(),
                applicationId,
            );
        } catch (e) {
            return handleError(e, 'run credit commitments');
        } finally {
            setLoading(false);
        }
    };

    const saveLSFormData = async (appId: string) => {
        if (!appLs || !appLs[appId]) {
            return;
        }

        // remove 2nd applicant incomings data for single applicant
        if (
            appLs[appId].factFindForm.applicants?.length === 1 &&
            appLs[appId].factFindForm.incomings?.length === 2
        ) {
            appLs[appId].factFindForm.incomings.pop();
        }

        setLoading(true);

        //merge each transformed state into  single object
        const requestData = merge(
            appLs[appId].factFindForm.loanDetails,
            { applicants: appLs[appId].factFindForm.applicants },
            { propertyDetails: appLs[appId].factFindForm.propertyDetails },
            { applicants: appLs[appId].factFindForm.incomings },
            appLs[appId].factFindForm.outgoings,
        );

        const applicationData = await postStoredApplication(
            appId,
            {
                accessToken: await getAccessToken(),
                idToken: await getIdToken(),
            },
            requestData,
        );

        if (applicationData.success) {
            //on success delete ls
            setAppLs((prevData) => ({
                ...prevData,
                [appId]: undefined,
            }));
        }

        readyForQuote = true;

        // get latest data
        await fetchApplicationData();
        return true;
    };

    const updateStageToFactFind = async (
        applicationId: string,
        currentStage: ApplicationStage,
    ) => {
        const stage = currentStage || applicationStage;

        if (
            stage === ApplicationStage.FACT_FIND ||
            stage === ApplicationStage.DECISIONING_ACCEPT ||
            stage === ApplicationStage.DECISIONING_DECLINE ||
            stage === ApplicationStage.CLOSED
        ) {
            return;
        }

        if (applicationId) {
            const result = await patchStageToFactFind(applicationId);
            if (result.success) {
                setApplicationStage(ApplicationStage.FACT_FIND);
            }
        }
    };

    const updatePhoneNumber = async (
        mobilePhoneNumber: string,
        getExternalApplicationId: string,
        applicationId: string,
    ) => {
        try {
            setLoading(true);
            return await savePhoneNumber(
                mobilePhoneNumber,
                getExternalApplicationId,
                applicationId,
            );
        } catch (e) {
            return handleError(e, 'save phone number');
        } finally {
            setLoading(false);
        }
    };

    const saveQuestionnaireAnswers = async (
        applicationId: string,
        data: any,
    ) => {
        try {
            setLoading(true);
            const response = await saveAnswers(applicationId, data);

            setPreferableOffers(response.preferableOffers);

            return response;
        } catch (e) {
            return handleError(e, 'save questionnaire answers');
        } finally {
            setLoading(false);
        }
    };

    const saveFinalStage = async (
        stageToSave: ApplicationFinalStage,
        applicationStage: ApplicationStage,
    ) => {
        const allowedFinalStages = [
            ApplicationFinalStage.SQL,
            ApplicationFinalStage.CLOSED,
        ];
        const currentStage = underwriting?.stageName;

        if (currentStage && !allowedFinalStages.includes(currentStage)) {
            await patchApplicationStage(
                applicationId,
                stageToSave,
                applicationStage,
                await getAccessToken(),
                await getIdToken(),
            );
        }
    };

    return (
        <ApplicationContext.Provider
            value={{
                loading,
                setLoading,
                getAppData,
                applicants,
                loanDetails,
                propertyDetails,
                outgoings,
                incomings,
                setIncomings,
                saveApplicants,
                saveLoanDetails,
                savePropertyDetails,
                saveOutgoings,
                saveIncomings,
                activeApplicant,
                setActiveApplicant,
                dashboardItemStatuses,
                fetchApplication,
                offers,
                setOffers,
                applicationStage,
                setApplicationStage,
                hasDashboardError,
                currentForm,
                setCurrentForm,
                runCreditCommitments,
                runDecisioning,
                applicationId,
                intercomData,
                saveLSFormData,
                updateStageToFactFind,
                externalApplicationId,
                setExternalApplicationId,
                updatePhoneNumber,
                applicationEmail,
                callDateTime,
                setCallDateTime,
                debtConUser,
                secondApplicantDeferred,
                selectedProduct,
                saveQuestionnaireAnswers,
                preferableOffers,
                needsAndPreferencesCompleted,
                setNeedsAndPreferencesCompleted,
                isApplicationUpdated,
                saveFinalStage,
                sentryLog,
                flushLogs,
                recommendedOffers,
                setRecommendedOffers,
                hasDeclinedOffer,
                setHasDeclinedOffer,
            }}
        >
            {children}
        </ApplicationContext.Provider>
    );
};

export default ApplicationContextProvider;
